import type { KeyboardEventHandler } from 'react';
import { useRef } from 'react';

import { Input } from '@poseidon/components/Input2';

interface Props {
  /** Controlled field text — the parent owns it (the wait editor's unit toggle re-expresses the same text). */
  value: string;
  onChange: (value: string) => void;
  /** Commit the current text (the parent parses / clamps / persists). Fired on blur / Enter — NOT after Escape. */
  onCommit: () => void;
  min: number;
  max: number;
  /** Focus + select the field on mount — set when opened in a popover so it's ready to type. */
  autoFocus?: boolean;
  className?: string;
}

/**
 * The shared compact "N" number field behind the timing pills — a `wait` duration and an `await_connect`
 * acceptance deadline. It owns only the commit-on-blur / Enter mechanics and the **Escape-cancels** guard;
 * the value and how it's parsed live in the parent (the wait editor's unit toggle re-expresses the same
 * text, so the state can't move down here).
 *
 * Escape sets a cancel flag and blurs, so the blur that follows — including the one fired when the
 * containing popover unmounts — SKIPS the commit instead of racing it. This is the same guard
 * {@link SwitchRangeChip} uses; without it, Escape would clamp-and-commit the in-progress value (the
 * popover's Escape unmounts the field → its blur commits) instead of discarding it.
 */
export function DaysNumberInput({ value, onChange, onCommit, min, max, autoFocus, className }: Props) {
  // Set on Escape so the blur that follows (or the popover unmounting) cancels instead of committing.
  const cancelledRef = useRef(false);

  const onBlur = () => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    onCommit();
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    else if (e.key === 'Escape') {
      cancelledRef.current = true;
      e.currentTarget.blur();
    }
  };

  return (
    <Input
      type="number"
      min={min}
      max={max}
      value={value}
      autoFocus={autoFocus}
      onFocus={(e) => e.target.select()}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      className={className}
    />
  );
}
